import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import useWishlistStore from '../store/wishlistStore';
import useCartStore from '../store/cartStore';
import { toast } from 'sonner';

export const Wishlist = () => {
  const { items, removeItem, clearWishlist } = useWishlistStore();
  const addToCart = useCartStore(state => state.addItem);

  const handleRemove = (productId, productName) => {
    removeItem(productId);
    toast.success(`Removed ${productName} from Wishlist`);
  };

  const handleMoveToCart = (product) => {
    // Add to cart with default size (user can change in cart)
    addToCart(product, '', '', null, 1);
    removeItem(product.id);
    toast.success('Moved to Cart');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-8 pb-16 px-4" data-testid="wishlist-empty">
        <div className="max-w-4xl mx-auto text-center py-20">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1A1A1A]/5 flex items-center justify-center">
            <Heart className="w-10 h-10 text-[#1A1A1A]/30" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl font-light text-[#1A1A1A] mb-4">Your Wishlist is Empty</h1>
          <p className="text-[#1A1A1A]/60 mb-8 max-w-md mx-auto">
            Save your favorite items to buy them later. Click the heart icon on any product to add it to your wishlist.
          </p>
          <Link 
            to="/products"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-8 py-4 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#333] transition-colors"
          >
            Start Shopping
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-8 pb-16 px-4 md:px-8" data-testid="wishlist-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A]">
              My <span className="text-[#C59746]">Wishlist</span>
            </h1>
            <p className="text-sm text-[#1A1A1A]/60 mt-1">{items.length} item{items.length !== 1 ? 's' : ''} saved</p>
          </div>
          {items.length > 0 && (
            <button
              onClick={() => {
                clearWishlist();
                toast.success('Wishlist cleared');
              }}
              className="text-sm text-red-500 hover:text-red-600 transition-colors flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>

        {/* Wishlist Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((product) => (
            <div 
              key={product.id} 
              className="group bg-white border border-[#1A1A1A]/10 hover:shadow-lg transition-shadow"
              data-testid={`wishlist-item-${product.id}`}
            >
              {/* Image */}
              <Link to={`/product/${product.id}`} className="block relative aspect-[3/4] overflow-hidden">
                <img 
                  src={product.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80'} 
                  alt={product.name}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${!product.in_stock ? 'opacity-50' : ''}`}
                />
                {!product.in_stock && (
                  <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
                    <span className="bg-[#1A1A1A]/80 text-white px-3 py-1 text-xs tracking-wider uppercase">
                      Sold Out
                    </span>
                  </div>
                )}
                {/* Remove Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemove(product.id, product.name);
                  }}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                  aria-label="Remove from Wishlist"
                >
                  <Heart className="w-4 h-4 fill-current" />
                </button>
              </Link>

              {/* Info */}
              <div className="p-4">
                <Link to={`/product/${product.id}`}>
                  <h3 className="text-sm text-[#1A1A1A] group-hover:text-[#C59746] transition-colors line-clamp-2 mb-2">
                    {product.name}
                  </h3>
                </Link>
                
                <div className="flex items-baseline gap-2 mb-4">
                  {product.discount_price ? (
                    <>
                      <span className="text-base font-medium text-[#1A1A1A]">₹{product.discount_price.toLocaleString()}</span>
                      <span className="text-sm text-[#1A1A1A]/50 line-through">₹{product.price.toLocaleString()}</span>
                    </>
                  ) : (
                    <span className="text-base font-medium text-[#1A1A1A]">₹{product.price.toLocaleString()}</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Link
                    to={`/product/${product.id}`}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#1A1A1A] text-white py-2.5 text-xs tracking-wider uppercase font-medium hover:bg-[#333] transition-colors"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    View
                  </Link>
                  <button
                    onClick={() => handleRemove(product.id, product.name)}
                    className="w-10 flex items-center justify-center border border-[#1A1A1A]/20 text-[#1A1A1A]/60 hover:border-red-500 hover:text-red-500 transition-colors"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Continue Shopping */}
        <div className="text-center mt-12">
          <Link 
            to="/products"
            className="inline-flex items-center gap-2 text-sm tracking-wider uppercase text-[#1A1A1A] hover:text-[#C59746] transition-colors"
          >
            Continue Shopping
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
